import React, { useEffect, useRef, useState } from "react";

/**
 * Wheel-style scroll picker (iOS-feel). Uses CSS scroll-snap.
 * - `options`: array of { value, label }
 * - `value`: currently-selected `value` (must exist in `options`, or null)
 * - `onChange`: (value) => void
 * - `height`: visible height of the picker (px)
 * - `itemHeight`: row height (px)
 */
export default function WheelPicker({
    options,
    value,
    onChange,
    height = 180,
    itemHeight = 40,
    testId,
    suffix,
}) {
    const scrollRef = useRef(null);
    const debounceRef = useRef(null);
    const [internalValue, setInternalValue] = useState(value);

    // Scroll to selected on mount and when value changes externally
    useEffect(() => {
        if (!scrollRef.current) return;
        const idx = options.findIndex(o => o.value === value);
        if (idx < 0) return;
        scrollRef.current.scrollTop = idx * itemHeight;
        setInternalValue(value);
    }, [value, options, itemHeight]);

    const handleScroll = () => {
        if (!scrollRef.current) return;
        const idx = Math.round(scrollRef.current.scrollTop / itemHeight);
        const opt = options[Math.max(0, Math.min(options.length - 1, idx))];
        if (!opt) return;
        if (opt.value !== internalValue) {
            setInternalValue(opt.value);
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (opt.value !== value) onChange(opt.value);
        }, 80);
    };

    const padding = (height - itemHeight) / 2;

    return (
        <div className="relative select-none" style={{ height }} data-testid={testId}>
            {/* Center highlight */}
            <div
                aria-hidden
                className="absolute left-0 right-0 pointer-events-none border-y border-burgundy/30 bg-burgundy/[0.04]"
                style={{ top: padding, height: itemHeight }}
            />
            {/* Top + bottom fade */}
            <div
                aria-hidden
                className="absolute inset-x-0 top-0 pointer-events-none"
                style={{ height: padding, background: "linear-gradient(to bottom, var(--bg-cream, #FAF6F1), transparent)" }}
            />
            <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{ height: padding, background: "linear-gradient(to top, var(--bg-cream, #FAF6F1), transparent)" }}
            />
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="h-full overflow-y-auto no-scrollbar"
                style={{
                    scrollSnapType: "y mandatory",
                    paddingTop: padding,
                    paddingBottom: padding,
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {options.map((opt) => {
                    const active = opt.value === internalValue;
                    return (
                        <button
                            type="button"
                            key={opt.value}
                            onClick={() => {
                                if (scrollRef.current) {
                                    const idx = options.findIndex(o => o.value === opt.value);
                                    scrollRef.current.scrollTo({ top: idx * itemHeight, behavior: "smooth" });
                                }
                            }}
                            className={`w-full flex items-center justify-center font-display transition-all ${
                                active ? "text-burgundy text-2xl" : "text-ink-muted text-lg opacity-60"
                            }`}
                            style={{ height: itemHeight, scrollSnapAlign: "center", scrollSnapStop: "always" }}
                            data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                        >
                            {opt.label}{suffix && active ? <span className="ml-1 text-xs tracking-[0.22em] uppercase text-ink-light">{suffix}</span> : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
